import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Download, Search, Shield, Users, UserCheck, UserX, Calendar
} from 'lucide-react';
import { specialEventsService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import { formatDate } from '../../utils/dateUtils';

const AdminBcParticipationReport = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [participated, setParticipated] = useState([]);
  const [notParticipated, setNotParticipated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('participated'); // 'participated' | 'notParticipated'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await specialEventsService.bcParticipationReport();
      setEvents(data.events || []);
      setParticipated(data.participated || []);
      setNotParticipated(data.notParticipated || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const filteredParticipated = useMemo(() => {
    if (!searchTerm) return participated;
    const term = searchTerm.toLowerCase();
    return participated.filter(r =>
      r.userName?.toLowerCase().includes(term) ||
      (r.characters || []).some(c => c.toLowerCase().includes(term))
    );
  }, [participated, searchTerm]);

  const filteredNotParticipated = useMemo(() => {
    if (!searchTerm) return notParticipated;
    const term = searchTerm.toLowerCase();
    return notParticipated.filter(r =>
      r.userName?.toLowerCase().includes(term) ||
      (r.characters || []).some(c => c.toLowerCase().includes(term))
    );
  }, [notParticipated, searchTerm]);

  const handleExportCSV = () => {
    const isParticipated = activeTab === 'participated';
    const rows = isParticipated ? filteredParticipated : filteredNotParticipated;
    const headers = isParticipated
      ? ['Membro', 'Personagens', 'Eventos participados']
      : ['Membro', 'Personagens'];
    const csvRows = rows.map(r => isParticipated
      ? [r.userName, (r.characters || []).join(' | '), (r.events || []).join(' | ')]
      : [r.userName, (r.characters || []).join(' | ')]
    );
    const csv = [headers.join(';'), ...csvRows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(';'))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bc-${isParticipated ? 'participaram' : 'nao-participaram'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingPage message="Carregando relatório..." title="Participação em BC" />;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 overflow-hidden">
      <div className="max-w-full mx-auto flex flex-col gap-4 h-full min-h-0 w-full">

        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-white/10 shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate('/app/admin')}
              className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-gray-300 hover:text-white transition-all flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent flex items-center gap-3">
                <Shield className="w-7 h-7 text-amber-400" />
                Participação nos Últimos 4 BC
              </h1>
              <p className="text-gray-400 mt-1 text-sm">
                Membros que participaram (ou não) dos últimos 4 eventos especiais do tipo BC
              </p>
            </div>
          </div>

          {/* Eventos considerados */}
          <div className="flex flex-wrap gap-2 mb-4">
            {events.length === 0 ? (
              <span className="text-sm text-gray-500">Nenhum evento BC encontrado</span>
            ) : events.map(e => (
              <span key={e.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
                <Calendar className="w-3 h-3" />
                {e.name}{e.eventDate ? ` • ${formatDate(e.eventDate)}` : ''}
              </span>
            ))}
          </div>

          {/* Busca + Export */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Search className="w-3 h-3" /> Buscar
              </label>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Nome do membro ou personagem..."
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50" />
            </div>
            <button onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-green-400 text-sm font-medium transition-all">
              <Download className="w-4 h-4" /> Exportar CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="flex-shrink-0 bg-red-900/20 rounded-xl p-4 border border-red-500/20 text-red-400 text-center">{error}</div>
        )}

        {/* Tabs */}
        <div className="flex-shrink-0 flex gap-3">
          <button onClick={() => setActiveTab('participated')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'participated' ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-500/20' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
            <UserCheck className="w-4 h-4" /> Participaram ({filteredParticipated.length})
          </button>
          <button onClick={() => setActiveTab('notParticipated')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'notParticipated' ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-lg shadow-red-500/20' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
            <UserX className="w-4 h-4" /> Não Participaram ({filteredNotParticipated.length})
          </button>
        </div>

        {/* Tab: Participaram */}
        {activeTab === 'participated' && (
          <div className="flex-1 min-h-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-sm" style={{ minWidth: '640px' }}>
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-white/10 bg-slate-800/95">
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Membro</th>
                    <th className="text-left px-3 py-3 text-gray-400 font-medium">Personagens</th>
                    <th className="text-center px-3 py-3 text-gray-400 font-medium whitespace-nowrap">BC participados</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipated.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-16 text-gray-400">
                        <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Nenhum membro participou</p>
                      </td>
                    </tr>
                  ) : filteredParticipated.map((row, idx) => (
                    <tr key={row.userId || idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {row.userName?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="text-white font-medium whitespace-nowrap">{row.userName}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(row.characters || []).map((n, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-xs text-gray-300 whitespace-nowrap">
                              {n}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold">
                          {(row.events || []).length}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab: Não Participaram */}
        {activeTab === 'notParticipated' && (
          <div className="flex-1 min-h-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-sm" style={{ minWidth: '480px' }}>
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-white/10 bg-slate-800/95">
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Membro</th>
                    <th className="text-left px-3 py-3 text-gray-400 font-medium">Personagens</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotParticipated.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="text-center py-16 text-gray-400">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Todos os membros participaram 🎉</p>
                      </td>
                    </tr>
                  ) : filteredNotParticipated.map((row, idx) => (
                    <tr key={row.userId || idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {row.userName?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="text-white font-medium whitespace-nowrap">{row.userName}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(row.characters || []).length === 0 ? (
                            <span className="text-gray-600 text-xs">—</span>
                          ) : (row.characters || []).map((n, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-xs text-gray-300 whitespace-nowrap">
                              {n}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBcParticipationReport;
